/**
 * One input per field the server says is still missing at the confirm-new-deal
 * step — the loan purpose, city, state, initial amount and fully funded amount —
 * each with its message under it. The pick-lists (purpose, state) are the
 * catalogue's own, the same ones Manual Deal Entry offers.
 */
import { useQuery } from '@tanstack/react-query'

import { dealFileService } from '@/services/dealFile'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import type { RequiredGap } from './requiredAtConfirm'

const BOX =
  'w-full rounded border bg-stone-900 px-2.5 py-1.5 text-[12px] text-stone-100 focus:outline-none '

export default function RequiredAtConfirmFields({
  gaps,
  answers,
  errors,
  disabled,
  onChange,
}: {
  gaps: ReadonlyArray<RequiredGap>
  answers: Record<string, string>
  errors: Record<string, string>
  disabled?: boolean
  onChange: (field: string, value: string) => void
}) {
  const catalogue = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
    queryFn: () => dealFileService.getCatalogue(),
    staleTime: 5 * 60 * 1000,
  })
  const optionsOf = (name: string) => catalogue.data?.fields.find((f) => f.name === name)?.options ?? []

  return (
    <fieldset className="space-y-3" data-testid="required-at-confirm">
      <legend className="text-[10px] uppercase tracking-wide text-stone-500">
        Required before the deal can be created
      </legend>
      {gaps.map((gap) => {
        const error = errors[gap.field] ?? (gap.message !== 'Required' ? gap.message : '')
        const options = gap.field === 'purpose' || gap.field === 'state' ? optionsOf(gap.field) : []
        const border = error ? 'border-red-500/50' : 'border-stone-700 focus:border-amber-400/60'
        return (
          <label key={gap.field} className="block space-y-1">
            <span className="block text-[10px] text-stone-400">
              {gap.label} <span className="text-red-400">*</span>
            </span>
            {options.length > 0 ? (
              <select
                aria-label={gap.label}
                value={answers[gap.field] ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(gap.field, e.target.value)}
                className={BOX + border}
              >
                <option value="">—</option>
                {options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                aria-label={gap.label}
                value={answers[gap.field] ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(gap.field, e.target.value)}
                placeholder={gap.field.endsWith('amount') ? 'e.g. 30,000,000' : undefined}
                className={BOX + border}
              />
            )}
            {error ? <span className="block text-[10px] text-red-400">{error}</span> : null}
          </label>
        )
      })}
    </fieldset>
  )
}
